import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Trash2, Search, Mail, Building2, User, Globe, FileCheck2, Award, GraduationCap, ArrowRight } from "lucide-react";
import AccountTalent from "./AccountTalent";
import ProfessionFilter from "./ProfessionFilter";
import AdminSearchAccess from "./AdminSearchAccess";
import AdminLevelRequests from "./AdminLevelRequests";
import AdminPager, { usePagedList } from "./AdminPager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  submitted: "Podaná žiadosť",
  paid: "Zaplatené",
  approved: "Schválená",
};

function fmtDate(s) {
  try { return new Date(s).toLocaleDateString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return s; }
}

export default function AdminAccounts({ token, onOpenMentorSign }) {
  const [audits, setAudits] = useState([]);
  const [q, setQ] = useState("");
  const [profF, setProfF] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const list = usePagedList(`${API}/admin/applicants`, token, { q, profs: profF.join(",") });
  const { items, setItems, loading, reload: fetchItems } = list;
  const mentors = list.mentors || [];

  const loadAudits = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/mentor-audits`, { params: { status: "pending" }, ...auth }); setAudits(data.audits || []); }
    catch { /* ignore */ }
  }, [token]);

  useEffect(() => { loadAudits(); }, [loadAudits]);

  const setMentor = async (a) => {
    const on = !a.is_mentor;
    let cert = a.mentor_certificate_no || "";
    if (on) { const v = window.prompt("Certifikačné číslo mentora (napr. Sa.Eko.Prs.2026-0001) — zobrazí sa na protokole:", cert || "Sa.Eko.Prs.2026-0001"); if (v === null) return; cert = v.trim(); }
    try {
      await axios.post(`${API}/admin/applicants/${a.id}/mentor`, { is_mentor: on, mentor_certificate_no: cert }, auth);
      setItems((list) => list.map((x) => x.id === a.id ? { ...x, is_mentor: on, mentor_certificate_no: cert } : x));
      fetchItems();
    } catch { alert("Nepodarilo sa zmeniť rolu mentora."); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`Naozaj NATRVALO zmazať účet ${acc.email}? Zmaže sa celý účet vrátane žiadosti, súborov, platieb, korešpondencie, položiek „Na podpis“ a podpísaných dokladov. Zápis v Sieni slávy (Tigik) treba odstrániť ručne. E-mail sa bude dať znovu zaregistrovať.`)) return;
    try {
      await axios.delete(`${API}/admin/applicants`, { params: { email: acc.email, applicant_id: acc.id }, ...auth });
      fetchItems();
    } catch { alert("Zmazanie zlyhalo."); }
  };

  const provisionMentor = async () => {
    const email = window.prompt("E-mail mentorského účtu:", "sauber.eko@gmail.com");
    if (email === null) return;
    const name = window.prompt("Meno mentora:", "Martin Smrkta");
    if (name === null) return;
    const cert = window.prompt("Certifikačné číslo mentora:", "Sa.Eko.Prs.2026-0001");
    if (cert === null) return;
    const password = window.prompt("Heslo (nechajte prázdne = ponechať existujúce; pri novom účte je povinné):", "");
    if (password === null) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/mentor-account`,
        { email: email.trim(), name: name.trim(), certificate_no: cert.trim(), password: password.trim(), type: "individual" },
        auth
      );
      alert(data.created ? "Mentorský účet bol vytvorený a schválený." : "Mentorský účet bol obnovený a schválený.");
      fetchItems();
    } catch (e) {
      alert(e.response?.data?.detail || "Obnova mentorského účtu zlyhala.");
    }
  };

  const filtered = items;

  return (
    <div data-testid="admin-accounts">
      <AdminSearchAccess token={token} />
      <AdminLevelRequests token={token} />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <p className="text-white/40 text-sm" data-testid="accounts-count">{list.all_total ?? list.total} registrovaných účtov{(q || profF.length) ? ` · nájdených ${list.total}` : ""}</p>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={provisionMentor} data-testid="provision-mentor"
            className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors">
            <GraduationCap size={15} /> Obnoviť mentorský účet
          </button>
          <button onClick={fetchItems} data-testid="accounts-refresh"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Obnoviť
          </button>
        </div>
      </div>

      {audits.length > 0 && (
        <button type="button" onClick={onOpenMentorSign} data-testid="admin-pending-audits"
          className="w-full mb-6 flex items-center justify-between gap-3 border border-amber-400/40 bg-amber-400/5 px-4 py-3 text-left hover:bg-amber-400/10 transition-colors">
          <span className="text-amber-300 text-sm font-semibold">Potvrdenia od mentorov čakajú na váš podpis ({audits.length})</span>
          <span className="inline-flex items-center gap-1.5 text-[#D4AF37] text-sm shrink-0" data-testid="open-mentorsign-link">Otvoriť „Na podpis" <ArrowRight size={15} /></span>
        </button>
      )}

      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="accounts-search"
          placeholder="Hľadať podľa mena, e-mailu alebo evidenčného čísla…"
          className="w-full bg-[#0a0a0a] border border-white/15 pl-10 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
      </div>
      <ProfessionFilter value={profF} onChange={setProfF} testid="accounts-prof-filter" />

      {filtered.length === 0 ? (
        <p className="text-white/40 text-center py-16">{loading ? "Načítavam…" : !(q || profF.length) ? "Zatiaľ žiadne registrované účty." : "Žiadny účet nezodpovedá hľadaniu."}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((a) => (
            <div key={a.id} data-testid="account-card" className="border border-white/10 bg-[#0a0a0a] p-5 hover:border-white/20 transition-colors">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-display text-lg flex items-center gap-2">
                    {a.type === "company" ? <Building2 size={16} className="text-[#D4AF37]" /> : <User size={16} className="text-[#D4AF37]" />}
                    {a.name || "—"}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-white/55">
                    <button type="button" onClick={() => { try { navigator.clipboard.writeText(a.email); } catch (e) {} }}
                      title="Kliknutím skopírujete e-mail (neotvára osobný e-mail)"
                      className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] cursor-pointer bg-transparent border-0 p-0 text-inherit"><Mail size={14} /> {a.email}</button>
                    <span className="inline-flex items-center gap-1.5 uppercase"><Globe size={14} /> {a.language}</span>
                    <span>{a.type === "company" ? "Firma" : "Jednotlivec"}</span>
                    <span className="text-white/35">Reg.: {fmtDate(a.created_at)}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {a.has_application ? (
                      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30">
                        <FileCheck2 size={12} /> {STATUS_LABEL[a.application_status] || a.application_status}
                        {a.evidence_number ? ` · ${a.evidence_number}` : ""}
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-white/5 text-white/40 border border-white/10">
                        Bez podanej žiadosti
                      </span>
                    )}
                    {a.certified && (
                      <span data-testid="account-certified-badge"
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/50">
                        <Award size={12} /> Certifikovaný
                      </span>
                    )}
                    {a.is_mentor && (
                      <span data-testid="account-mentor-badge"
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40">
                        <GraduationCap size={12} /> Mentor{a.mentor_certificate_no ? ` · ${a.mentor_certificate_no}` : ""}
                      </span>
                    )}
                  </div>
                  <AccountTalent a={a} mentors={mentors} token={token}
                    onChange={(n) => setItems((list) => list.map((x) => x.id === n.id ? n : x))} />
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => setMentor(a)} data-testid="account-mentor-toggle"
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border transition-colors ${a.is_mentor ? "border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10" : "border-white/15 text-white/60 hover:border-[#D4AF37]/50 hover:text-white"}`}
                    title="Zapnúť/vypnúť rolu mentora">
                    <GraduationCap size={14} /> {a.is_mentor ? "Zrušiť mentora" : "Nastaviť mentora"}
                  </button>
                  <button onClick={() => remove(a)} data-testid="account-delete"
                    className="text-white/40 hover:text-red-400 transition-colors" title="Zmazať účet">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <AdminPager page={list.page} pages={list.pages} total={list.total} onPage={list.setPage} testid="accounts-pager" />
    </div>
  );
}
